import React, { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Loader2, Search } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { listParties } from '@/app/services/partyService';
import { UserWithSites } from '@/app/services/userManagementService';

interface PartyAssignmentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: UserWithSites | null;
  onAssignParties: (partyIds: number[]) => Promise<void>;
  organizationId: number;
}

const PartyAssignmentModal: React.FC<PartyAssignmentModalProps> = ({
  open,
  onOpenChange,
  user,
  onAssignParties,
  organizationId,
}) => {
  const [selectedPartyIds, setSelectedPartyIds] = useState<number[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch all parties in the organization
  const { data: partiesData, isLoading: isLoadingParties } = useQuery({
    queryKey: ['parties', organizationId],
    queryFn: () => listParties({ limit: 1000 }, organizationId),
    enabled: !!organizationId && open,
  });

  // Initialize selected parties when user changes
  useEffect(() => {
    if (user?.managed_parties) {
      setSelectedPartyIds(user.managed_parties.map(p => p.id));
    } else {
      setSelectedPartyIds([]);
    }
  }, [user]);

  // Filter parties based on search
  const filteredParties = useMemo(() => {
    if (!partiesData?.items) return [];

    return partiesData.items.filter(party => {
      if (!searchQuery) return true;
      const query = searchQuery.toLowerCase();
      return (
        party.name.toLowerCase().includes(query) ||
        party.phone?.toLowerCase().includes(query) ||
        party.email?.toLowerCase().includes(query)
      );
    });
  }, [partiesData, searchQuery]);

  const handleToggleParty = (partyId: number) => {
    setSelectedPartyIds(prev =>
      prev.includes(partyId)
        ? prev.filter(id => id !== partyId)
        : [...prev, partyId]
    );
  };

  const handleSelectAll = () => {
    if (selectedPartyIds.length === filteredParties.length) {
      setSelectedPartyIds([]);
    } else {
      setSelectedPartyIds(filteredParties.map(p => p.id));
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await onAssignParties(selectedPartyIds);
      setSearchQuery('');
      onOpenChange(false);
    } catch (error) {
      // Error handling is done in parent component
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSearchQuery('');
    onOpenChange(false);
  };

  // Get counts for display
  const currentCount = user?.managed_parties?.length || 0;
  const changedCount = selectedPartyIds.length;
  const hasChanges = currentCount !== changedCount ||
    !user?.managed_parties?.every(p => selectedPartyIds.includes(p.id));

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Manage Party Access</DialogTitle>
          <DialogDescription>
            Select which parties {user?.full_name || user?.username} can access
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search parties..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Parties List */}
          {isLoadingParties ? (
            <div className="flex justify-center items-center h-48">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : filteredParties.length === 0 ? (
            <div className="text-center text-muted-foreground py-8">
              {searchQuery
                ? 'No parties found matching your search'
                : 'No parties available'}
            </div>
          ) : (
            <>
              {/* Select All */}
              <div className="flex items-center justify-between px-2 py-1 border-b">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="select-all"
                    checked={selectedPartyIds.length === filteredParties.length}
                    onCheckedChange={handleSelectAll}
                  />
                  <Label htmlFor="select-all" className="text-sm font-medium cursor-pointer">
                    Select All ({filteredParties.length})
                  </Label>
                </div>
                <span className="text-xs text-muted-foreground">
                  {selectedPartyIds.length} selected
                </span>
              </div>

              {/* Parties List */}
              <ScrollArea className="h-[300px] pr-4">
                <div className="space-y-2">
                  {filteredParties.map((party) => (
                    <div
                      key={party.id}
                      className="flex items-start space-x-3 p-3 rounded-md hover:bg-accent cursor-pointer"
                      onClick={() => handleToggleParty(party.id)}
                    >
                      <Checkbox
                        id={`party-${party.id}`}
                        checked={selectedPartyIds.includes(party.id)}
                        onCheckedChange={() => handleToggleParty(party.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div className="flex-1 space-y-1">
                        <Label
                          htmlFor={`party-${party.id}`}
                          className="text-sm font-medium cursor-pointer"
                        >
                          {party.name}
                        </Label>
                        <div className="flex flex-col gap-1">
                          {party.phone && (
                            <p className="text-xs text-muted-foreground">
                              {party.phone}
                            </p>
                          )}
                          {party.email && (
                            <p className="text-xs text-muted-foreground">
                              {party.email}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!hasChanges || isSubmitting}
          >
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PartyAssignmentModal;
